import { useEffect } from 'react'
import useSalesforceLead from './useSalesforceLead'

type LeadFields = (keyof SfLead)[]

/**
 * Provides a hook to handle Salesforce lead updates based on specified fields.
 *
 * This hook listens for changes in specified Salesforce lead fields and executes a callback function when changes are detected.
 * It leverages the `useSalesforceLead` hook to access the current Salesforce lead data, including any changed fields.
 *
 * @returns A function that accepts a callback and an array of fields to listen for changes. The callback is executed with the current Salesforce lead results when any of the specified fields have changed.
 *
 * @example
 * // In a component
 * import useHandleSalesforceLeadUpdate from 'hooks/salesforce/useSalesforceLeadUpdate';
 *
 * const handleLeadUpdate = useHandleSalesforceLeadUpdate();
 *
 * handleLeadUpdate(
 *   (results) => {
 *     // Handle the lead update
 *     console.log('Lead updated:', results);
 *   },
 *   ['Service_type__c'] // Fields to listen for changes
 * );
 *
 * This hook is useful for responding to changes in Salesforce lead data within React components, allowing for dynamic updates based on lead data changes.
 */
const useHandleSalesforceLeadUpdate = (): ((
  callback: (results: SalesForceLeadResults) => void,
  fieldsToListen: LeadFields
) => void) => {
  const { results } = useSalesforceLead()

  const changedFields = results?.changedFields || []

  return (callback, fieldsToListen) => {
    useEffect(() => {
      if (typeof callback === 'function') {
        const hasChangedFieldsToListen = changedFields.some((field) =>
          fieldsToListen.includes(field)
        )

        if (hasChangedFieldsToListen || fieldsToListen.length === 0) {
          callback(results)
        }
      }
    }, [callback, fieldsToListen, results])
  }
}

export default useHandleSalesforceLeadUpdate
